/**
 * Fixtures store — PWA-side local cache of the user's fixture rig.
 *
 * Two roles:
 *   1. When connected to OPBridge, the PWA reads `/fixtures` for the
 *      canonical list and mirrors it here so the timeline editor can
 *      render without re-fetching per lane.
 *   2. When offline (dev, first-run, disconnected), a seeded local list
 *      lets the user prototype timelines. Contains one built-in Chauvet
 *      SlimPAR 56 example so the PAR CAN use case works out of the box.
 *
 * `[CORRECTED 2026-09-17]` This said "when OPBridge ships
 * `GET /fixtures/profiles` the PWA will hydrate `cachedProfile` from there".
 * That endpoint shipped at OPBridge S141, named `GET /dmx/profiles`, and the
 * hydration never happened - so `BUILTIN_PROFILES` below, a table with ONE
 * entry, stayed the only profile source. Three of the five fixtures in the
 * operator's real rig resolved nothing at all. `fixtures/profileSync.ts` now
 * holds the live catalogue and `resolveFixtureProfile` reads it first; the
 * seed below is the offline fallback it always should have been.
 */

import { createSignal, type Accessor } from 'solid-js';
import {
  type FixtureInstance,
  type FixtureLibrary,
  type FixtureProfile,
  type FixtureProfileMode,
  type FixtureSubfixture,
  type FixtureCapabilityName,
  FIXTURES_SCHEMA_VERSION,
} from '@opbuttons/schema';
import { ulid } from '../utils/ulid.ts';
import { createHistory } from '../undo/history.ts';
import { bridgeFixtures, bridgeFixturesAuthoritative } from './bridgeSync.ts';
import { bridgeProfile } from './profileSync.ts';

const FIXTURES_KEY = 'opbuttons-fixtures-v1';

/** Chauvet SlimPAR 56, 3-channel RGB mode. Mirrors OPBridge's vendored
 *  profile at crates/opbridge-fixtures/manual/chauvet-dj/slimpar-56.json. */
const SLIMPAR_56_PROFILE: FixtureProfile = {
  id: 'chauvet-dj/slimpar-56',
  name: 'Chauvet SlimPAR 56',
  manufacturer: 'Chauvet DJ',
  model: 'SlimPAR 56',
  modes: [
    {
      name: '3-channel',
      channelCount: 3,
      capabilities: [
        { capability: 'color.r', channelIndex: 0, domain: { kind: 'linear' } },
        { capability: 'color.g', channelIndex: 1, domain: { kind: 'linear' } },
        { capability: 'color.b', channelIndex: 2, domain: { kind: 'linear' } },
      ],
    },
  ],
};

/** Built-in profile registry. Grows as OPBridge vendors more profiles.
 *  Keyed by profileId so fixture instances can resolve their profile
 *  offline. */
export const BUILTIN_PROFILES: Record<string, FixtureProfile> = {
  [SLIMPAR_56_PROFILE.id]: SLIMPAR_56_PROFILE,
};

function emptyLibrary(): FixtureLibrary {
  return { schemaVersion: FIXTURES_SCHEMA_VERSION, fixtures: [] };
}

function seedLibrary(): FixtureLibrary {
  const seedInstance: FixtureInstance = {
    id: ulid(),
    name: 'PAR 1',
    profileId: SLIMPAR_56_PROFILE.id,
    mode: '3-channel',
    universe: 1,
    dmxAddress: 1,
    cachedProfile: SLIMPAR_56_PROFILE,
  };
  return { schemaVersion: FIXTURES_SCHEMA_VERSION, fixtures: [seedInstance] };
}

function load(): FixtureLibrary {
  if (typeof localStorage === 'undefined') return seedLibrary();
  try {
    const raw = localStorage.getItem(FIXTURES_KEY);
    if (!raw) return seedLibrary();
    const parsed = JSON.parse(raw) as FixtureLibrary;
    if (parsed.schemaVersion !== FIXTURES_SCHEMA_VERSION) {
      console.warn(`fixtures: unknown schemaVersion ${parsed.schemaVersion}, starting empty`);
      return emptyLibrary();
    }
    if (!Array.isArray(parsed.fixtures)) return emptyLibrary();
    return parsed;
  } catch (err) {
    console.warn('fixtures: load failed', err);
    return emptyLibrary();
  }
}

function persist(lib: FixtureLibrary): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(FIXTURES_KEY, JSON.stringify(lib));
  } catch (err) {
    console.warn('fixtures: persist failed', err);
  }
}

const [library, setLibrary] = createSignal<FixtureLibrary>(load());

export const fixtureLibrary: Accessor<FixtureLibrary> = library;

/** The local library, always. Editors and undo operate on this. */
export const localFixtures: Accessor<FixtureInstance[]> = () => library().fixtures;

/**
 * The fixtures to SHOW. OPBridge owns the rig, so its list wins whenever it
 * answered (`bridgeFixturesAuthoritative`); otherwise this falls back to the
 * local library so the app still works offline, on an older bridge, or -
 * today - while `/fixtures` is refused for scope.
 *
 * Deliberately a read-side preference, not a merge: nothing here writes the
 * bridge's list into local storage. Merging two edited lists is a real design
 * question (who wins, what happens to a fixture the operator added offline)
 * and it cannot be answered honestly until the endpoint is reachable. Until
 * then, showing the authoritative list without silently overwriting local
 * work is the conservative half that is safe to ship.
 */
/**
 * Rig order, and it is load-bearing rather than cosmetic.
 *
 * `GET /fixtures` comes back in a DIFFERENT order on every call - OPBridge
 * stores fixtures in a map and serialises in iteration order, which was never
 * a promise. Measured on the live daemon: four consecutive calls, four
 * different orders. That costs nothing until something LISTS them, at which
 * point the Lights manager and every target picker reshuffle on each re-read
 * and the light you were about to tap moves out from under you.
 *
 * By universe, then address, then id. Not by NAME: a list ordered by patch
 * reads like a patch sheet, which is how a rig gets checked against the
 * physical wiring, and it puts an address conflict next to the thing it
 * conflicts with. `id` is the final tiebreak so two fixtures deliberately
 * sharing an address still hold a stable order instead of swapping.
 */
function byPatch(a: FixtureInstance, b: FixtureInstance): number {
  return (a.universe - b.universe)
    || (a.dmxAddress - b.dmxAddress)
    || a.id.localeCompare(b.id);
}

export const fixtures: Accessor<FixtureInstance[]> = () =>
  [...(bridgeFixturesAuthoritative() ? bridgeFixtures() : library().fixtures)].sort(byPatch);

export function getFixture(id: string): FixtureInstance | undefined {
  return library().fixtures.find((f) => f.id === id);
}

/**
 * Resolve a fixture instance's profile, best source first.
 *
 * `[REORDERED 2026-09-17]` - this used to be `cachedProfile ?? BUILTIN`, and
 * with `BUILTIN` holding exactly one profile that meant three of the five
 * fixtures in the operator's real rig resolved NOTHING. The live catalogue
 * from `GET /dmx/profiles` now goes first.
 *
 * 1. **OPBridge's catalogue** - twelve vendored profiles, current as of the
 *    last connect, and the only source that carries archetype / zones / FX.
 * 2. **The instance's `cachedProfile`** - whatever was stamped on it when it
 *    was created. Still right offline, possibly stale.
 * 3. **The built-in seed** - one profile, for a first run with no bridge.
 *
 * Deliberately NOT a merge. A profile is a single make/model definition, and
 * two disagreeing copies of one mean the newer one is right - there is no
 * half of a stale profile worth keeping.
 */
export function resolveFixtureProfile(instance: FixtureInstance): FixtureProfile | undefined {
  return bridgeProfile(instance.profileId)
    ?? instance.cachedProfile
    ?? BUILTIN_PROFILES[instance.profileId];
}

/** The mode an instance is patched in, or undefined. */
export function fixtureMode(instance: FixtureInstance): FixtureProfileMode | undefined {
  const profile = resolveFixtureProfile(instance);
  return profile?.modes.find((m) => m.name === instance.mode);
}

/** A fixture's addressable zones, in profile order. Empty for a single-block
 *  fixture. The `index` is what a DMX write passes as `subfixture`; omitting
 *  it addresses the whole fixture, which for a zoned fixture means the write
 *  FANS to every zone. */
export function fixtureZones(instance: FixtureInstance): FixtureSubfixture[] {
  return fixtureMode(instance)?.subfixtures ?? [];
}

/**
 * What a fixture can actually DO, as a flat set of capability names.
 *
 * `[BROADENED 2026-09-17]` This used to return the MODE's capability list
 * alone, which is empty for every zoned fixture in OPBridge's catalogue -
 * a zoned profile declares its capabilities on its ZONES and leaves the mode's
 * own array `[]`. Verified on the wire: the COLORband PiX's 12-channel mode
 * has `capabilities: []` and four Blocks each declaring color.r/g/b; the
 * 4PLAY and the Cubix 2.0 are the same shape. So a zoned fixture read as
 * "capable of nothing", which is how the operator's COLORband stayed
 * uncontrollable even after profile resolution was fixed.
 *
 * Found by the negative side of `fixtures_resolve_real_profiles` rather than
 * by reading: the profile resolved, the label came good, and the capability
 * count stayed at zero.
 *
 * Returns the UNION of the mode's own capabilities and every zone's. Union
 * rather than either alone, because both halves occur: a PL60C declares on
 * the mode and has no zones; a COLORband declares only on zones. A surface
 * asking "can this fixture take a colour?" wants one answer covering both.
 * A surface that needs to know WHICH zone does what asks `fixtureZones()`.
 */
export function fixtureCapabilityNames(instance: FixtureInstance): FixtureCapabilityName[] {
  const mode = fixtureMode(instance);
  if (!mode) return [];
  const out = new Set<FixtureCapabilityName>();
  for (const c of mode.capabilities) out.add(c.capability);
  for (const z of mode.subfixtures ?? []) for (const c of z.capabilities) out.add(c);
  return [...out];
}

/** Resolve the mode's own capability RECORDS on a fixture instance. Returns
 *  empty when profile/mode isn't found, and - correctly - for a zoned fixture
 *  whose capabilities all live on its zones. Prefer `fixtureCapabilityNames`
 *  for "what can this fixture do"; this stays for callers that want the
 *  records. */
export function fixtureCapabilities(instance: FixtureInstance) {
  return fixtureMode(instance)?.capabilities ?? [];
}

export function setFixtureLibrary(next: FixtureLibrary): void {
  fixtureHistory.checkpoint();
  setLibrary(next);
  persist(next);
}

/** History handle for the fixtures library. Covers add/update/remove
 *  fixture — everything that flows through `setFixtureLibrary`. */
export const fixtureHistory = createHistory<FixtureLibrary>({
  name: 'fixtures',
  read: () => library(),
  write: (next) => {
    setLibrary(next);
    persist(next);
  },
});

export function upsertFixture(f: FixtureInstance): void {
  const lib = library();
  const ix = lib.fixtures.findIndex((x) => x.id === f.id);
  const nextFixtures = ix === -1
    ? [...lib.fixtures, f]
    : lib.fixtures.map((x, i) => (i === ix ? f : x));
  setFixtureLibrary({ ...lib, fixtures: nextFixtures });
}
